// @mui
import { Box, BoxProps } from '@mui/material';
// hooks
import useResponsive from '../../hooks/useResponsive';
// config
import { HEADER, NAV } from '../../config-global';
// components
import { useTheme } from '@mui/material';
import { useConversationModalContext } from 'src/sections/@dashboard/conversation/hooks/useConversationModalContext';
import { useOrganizationContext } from 'src/sections/@dashboard/organization/hooks/useOrganizationContext';
import { useSettingsContext } from '../../components/settings';
import { useSidebarContext } from './SidebarContext';
// ----------------------------------------------------------------------

export const SPACING = 8;

type MainProps = BoxProps;

export default function Main({ children, sx, ...other }: MainProps) {
  const { themeLayout } = useSettingsContext();
  const { isOrganizationAdminWithoutOrganizations } = useOrganizationContext();
  const theme = useTheme();
  const { sidebarOpen, isProSidebar } = useSidebarContext();
  const {
    state: { open },
  } = useConversationModalContext();

  const isNavHorizontal = themeLayout === 'horizontal';

  const isNavMini = themeLayout === 'mini';

  const isDesktop = useResponsive('up', 'lg');

  const paddingTop = isOrganizationAdminWithoutOrganizations
    ? HEADER.H_DASHBOARD_DESKTOP_OFFSET + 40
    : isDesktop
    ? HEADER.H_DASHBOARD_DESKTOP_OFFSET + HEADER.H_TOOLBAR_DESKTOP + 40
    : HEADER.H_DASHBOARD_DESKTOP_OFFSET + 24;

  // Pro/admin sidebar layout on desktop
  if (isProSidebar && isDesktop) {
    const proSidebarPt = isOrganizationAdminWithoutOrganizations
      ? HEADER.H_DASHBOARD_DESKTOP_OFFSET + 40
      : HEADER.H_DASHBOARD_DESKTOP_OFFSET + 40;

    return (
      <Box
        component="main"
        sx={{
          position: 'relative',
          display: 'flex',
          flex: 1,
          minHeight: '100vh',
          backgroundColor: theme.palette.background.neutral,
          pt: `${proSidebarPt}px`,
          px: 3,
          pb: '24px',
          ml: sidebarOpen
            ? `${NAV.W_DASHBOARD}px`
            : `${NAV.W_DASHBOARD_MINI}px`,
          transition: theme.transitions.create(['margin-left'], {
            easing: theme.transitions.easing.sharp,
            duration: sidebarOpen
              ? theme.transitions.duration.enteringScreen
              : theme.transitions.duration.leavingScreen,
          }),
          ...sx,
        }}
        {...other}
      >
        {children}
      </Box>
    );
  }

  if (isNavHorizontal) {
    return (
      <Box
        component="main"
        sx={{
          position: 'relative',
          display: 'flex',
          flex: 1,
          minHeight: '100vh',
          backgroundColor: theme.palette.background.neutral,
          pt: `${paddingTop}px`,
          px: 2,
          pb: 2,
          ...(isDesktop && {
            px: 3,
            pt: `${paddingTop}px`,
            pb: `24px`,
          }),
        }}
      >
        {children}
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        position: 'relative',
        flexGrow: 1,
        py: `${HEADER.H_MOBILE + SPACING}px`,
        ...(isDesktop && {
          px: 3,
          py: `${HEADER.H_DASHBOARD_DESKTOP + SPACING}px`,
          width: `calc(100% - ${NAV.W_DASHBOARD}px)`,
          ...(isNavMini && {
            width: `calc(100% - ${NAV.W_DASHBOARD_MINI}px)`,
          }),
        }),
        ...sx,
      }}
      {...other}
    >
      {children}
    </Box>
  );
}
